import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { 
  FaReact, FaNodeJs, FaPython, FaDocker, FaAws,
  FaFigma, FaGitAlt, FaAngular, FaVuejs
} from 'react-icons/fa';
import { 
  SiTypescript, SiTailwindcss, SiMongodb, 
  SiPostgresql, SiNextdotjs, SiGraphql, SiFirebase,
  SiFlutter, SiRedux, SiExpress
} from 'react-icons/si';

const TechStack = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const categories = ['All', 'Frontend', 'Backend', 'Database', 'DevOps', 'Mobile', 'Design'];

  const technologies = [
    { name: 'React', category: 'Frontend', icon: React.createElement(FaReact as any) },
    { name: 'Next.js', category: 'Frontend', icon: React.createElement(SiNextdotjs as any) },
    { name: 'Vue.js', category: 'Frontend', icon: React.createElement(FaVuejs as any) },
    { name: 'Angular', category: 'Frontend', icon: React.createElement(FaAngular as any) },
    { name: 'TypeScript', category: 'Frontend', icon: React.createElement(SiTypescript as any) },
    { name: 'Tailwind CSS', category: 'Frontend', icon: React.createElement(SiTailwindcss as any) },
    { name: 'Redux', category: 'Frontend', icon: React.createElement(SiRedux as any) },
    { name: 'Node.js', category: 'Backend', icon: React.createElement(FaNodeJs as any) },
    { name: 'Python', category: 'Backend', icon: React.createElement(FaPython as any) },
    { name: 'GraphQL', category: 'Backend', icon: React.createElement(SiGraphql as any) },
    { name: 'Express', category: 'Backend', icon: React.createElement(SiExpress as any) },
    { name: 'MongoDB', category: 'Database', icon: React.createElement(SiMongodb as any) },
    { name: 'PostgreSQL', category: 'Database', icon: React.createElement(SiPostgresql as any) },
    { name: 'Firebase', category: 'Database', icon: React.createElement(SiFirebase as any) },
    { name: 'Docker', category: 'DevOps', icon: React.createElement(FaDocker as any) },
    { name: 'AWS', category: 'DevOps', icon: React.createElement(FaAws as any) },
    { name: 'Git', category: 'DevOps', icon: React.createElement(FaGitAlt as any) },
    { name: 'Figma', category: 'Design', icon: React.createElement(FaFigma as any) },
    { name: 'Flutter', category: 'Mobile', icon: React.createElement(SiFlutter as any) },
    { name: 'React Native', category: 'Mobile', icon: React.createElement(FaReact as any) },
  ];

  const filteredTech = activeCategory === 'All' 
    ? technologies 
    : technologies.filter(tech => tech.category === activeCategory);

  return (
    <section className="bg-svnate-darkNavy py-24 md:py-32 px-6 md:px-12 relative" id="tech-stack">
      <div className="container mx-auto relative z-10 max-w-7xl">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mb-16 md:mb-24 text-center"
        >
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full border border-white/10 bg-[#0f1419]">
            <span className="text-svnate-lightTeal font-semibold text-sm">Technology</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-6">
            Our Engineering Core
          </h2>
          <p className="text-lg md:text-xl text-foreground/70 max-w-2xl mx-auto font-serif">
            We don't just use tools; we master them. Explore the cutting-edge ecosystem we use to build robust, scalable, and future-proof solutions.
          </p>
        </motion.div>

        {/* IDE Terminal UI */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="w-full mx-auto bg-[#0a0c10] border border-white/10 rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)] flex flex-col"
        >
          {/* macOS Title Bar */}
          <div className="h-14 bg-white/[0.02] border-b border-white/10 flex items-center px-6 relative shrink-0">
            <div className="flex gap-2.5 absolute left-6">
              <div className="w-3.5 h-3.5 rounded-full bg-red-500/80 hover:bg-red-500 cursor-pointer transition-colors"></div>
              <div className="w-3.5 h-3.5 rounded-full bg-yellow-500/80 hover:bg-yellow-500 cursor-pointer transition-colors"></div>
              <div className="w-3.5 h-3.5 rounded-full bg-green-500/80 hover:bg-green-500 cursor-pointer transition-colors"></div>
            </div>
            <div className="mx-auto text-sm font-mono text-white/30 flex items-center gap-2">
              <span className="text-svnate-lightTeal">~/svnate</span>/tech-stack.ts
            </div>
          </div>

          <div className="flex flex-col md:flex-row flex-1 min-h-[600px]">
            
            {/* Mobile Dropdown Header */}
            <div className="md:hidden p-4 border-b border-white/5 bg-white/[0.01] shrink-0">
               <button 
                 onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                 className="w-full flex items-center justify-between bg-[#0f1419] border border-white/10 px-5 py-4 rounded-xl text-white font-medium"
               >
                 <span className="flex items-center gap-2">
                   <span className="text-white/50 text-xs tracking-widest uppercase">Category:</span>
                   {activeCategory}
                 </span>
                 <ChevronDown className={`w-5 h-5 text-white/50 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} />
               </button>
               
               <AnimatePresence>
                 {isDropdownOpen && (
                   <motion.div 
                     initial={{ height: 0, opacity: 0 }}
                     animate={{ height: 'auto', opacity: 1 }}
                     exit={{ height: 0, opacity: 0 }}
                     className="overflow-hidden mt-2 rounded-xl border border-white/10 bg-[#0a0c10]"
                   >
                     <ul className="py-2">
                       {categories.map(cat => (
                         <li key={cat}>
                            <button 
                              onClick={() => {
                                setActiveCategory(cat);
                                setIsDropdownOpen(false);
                              }}
                              className={`w-full text-left px-5 py-3 text-sm font-medium transition-all ${
                                activeCategory === cat ? 'bg-svnate-teal/10 text-svnate-lightTeal' : 'text-white/50 hover:bg-white/5 hover:text-white/80'
                              }`}
                            >
                              {cat}
                            </button>
                         </li>
                       ))}
                     </ul>
                   </motion.div>
                 )}
               </AnimatePresence>
            </div>

            {/* Desktop Sidebar (hidden on mobile) */}
            <div className="hidden md:block w-64 bg-white/[0.01] border-r border-white/5 p-6 overflow-y-auto shrink-0">
               <div className="text-xs font-semibold text-white/30 uppercase tracking-widest mb-6 px-2">
                 Categories
               </div>
               <ul className="space-y-1">
                 {categories.map(cat => (
                   <li key={cat}>
                      <button 
                        onClick={() => setActiveCategory(cat)}
                        className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all flex items-center justify-between ${
                          activeCategory === cat 
                            ? 'bg-svnate-teal/10 text-svnate-lightTeal shadow-[inset_0_0_0_1px_rgba(110,231,183,0.2)]' 
                            : 'text-white/50 hover:bg-white/5 hover:text-white/80'
                        }`}
                      >
                        {cat}
                        {activeCategory === cat && (
                          <motion.div 
                            layoutId="activeIndicator"
                            className="w-1.5 h-1.5 rounded-full bg-svnate-lightTeal"
                          />
                        )}
                      </button>
                   </li>
                 ))}
               </ul>
            </div>

            {/* Main Content Grid */}
            <div className="flex-1 p-6 md:p-10 bg-[#080b0e] overflow-y-auto custom-scrollbar">
               <motion.div layout className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
                 <AnimatePresence mode="popLayout">
                    {filteredTech.map(tech => (
                       <motion.div
                         layout
                         initial={{ opacity: 0, scale: 0.8, filter: 'blur(10px)' }}
                         animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                         exit={{ opacity: 0, scale: 0.8, filter: 'blur(10px)' }}
                         transition={{ duration: 0.4, type: "spring", bounce: 0.3 }}
                         key={tech.name}
                         className="flex flex-col items-center justify-center p-6 md:p-8 bg-white/[0.02] border border-white/5 rounded-2xl hover:bg-white/[0.04] hover:border-white/20 hover:-translate-y-1 transition-all group cursor-pointer"
                       >
                         <div className="text-4xl md:text-5xl text-white/20 group-hover:text-white mb-4 transition-colors duration-300">
                           {tech.icon}
                         </div>
                         <div className="text-xs font-mono text-white/40 group-hover:text-svnate-lightTeal transition-colors duration-300 text-center">
                           {tech.name}
                         </div>
                       </motion.div>
                    ))}
                 </AnimatePresence>
               </motion.div>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default TechStack;
